import { css } from "@linaria/core";
import { type CSSProperties, type ReactNode, useState } from "react";
import { App, Avatar, Button, Card, Dropdown, Flex, Layout, Menu, type MenuProps, Typography } from "antd";
import {
  BookOutlined,
  CloseOutlined,
  ExperimentOutlined,
  LogoutOutlined,
  SettingOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { BreadcrumbTrailProvider } from "../context/BreadcrumbTrailProvider.tsx";
import { CostDisplayProvider } from "../context/CostDisplayContext.tsx";
import { CurrentSessionProvider } from "../context/CurrentSessionContext.tsx";
import { FreeAccessBanner } from "../components/FreeAccessBanner.tsx";
import { TeamRole, type UserFragment } from "../gql/generated/graphqlSchema.tsx";
import { HEADER_HEIGHT } from "./constants.ts";
import { routes } from "../routes.ts";
import { LanguageSwitcher } from "../components/LanguageSwitcher.tsx";
import LogoSvg from "../assets/logo.svg?react";

const { Header, Sider, Content } = Layout;

const root = css`
  min-height: 100vh;
  /* --calculabel-header-h is set via inline style from layout/constants.ts */
`;

const siderStyle = css`
  border-right: 1px solid var(--ant-color-split);
  position: sticky;
  top: 0;
  height: 100vh;
  overflow-y: auto;
  box-shadow: var(--ant-box-shadow-tertiary);
`;

const logoStyle = css`
  height: var(--calculabel-header-h);
  border-bottom: 1px solid var(--ant-color-split);
  overflow: hidden;
  background-color: #479f76;
`;

const logoIcon = css`
  width: var(--ant-size-xxl);
  height: var(--ant-size-xxl);
  flex-shrink: 0;
  border: var(--ant-line-width) solid var(--ant-color-white);
  padding: var(--ant-padding-xs);
  border-radius: var(--ant-border-radius);
`;

const logoText = css`
  font-size: var(--ant-font-size-lg);
  white-space: nowrap;
  margin-left: var(--ant-margin-sm);
  color: var(--ant-color-white);
  text-transform: uppercase;
`;

const sidebarMenu = css`
  border-right: none;
  padding-top: var(--ant-padding-xs);
`;

const siderInner = css`
  height: calc(100% - var(--calculabel-header-h));
`;

const langSwitcherWrap = css`
  margin-top: auto;
  display: flex;
  justify-content: center;
  padding: var(--ant-padding-xs);
  border-top: 1px solid var(--ant-color-split);
`;

const headerStyle = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  background: var(--ant-color-bg-container);
  padding: 0 var(--ant-padding-lg);
  border-bottom: 1px solid var(--ant-color-split);
  position: sticky;
  top: 0;
  z-index: 10;
  box-shadow: var(--ant-box-shadow-tertiary);
`;

const userTrigger = css`
  display: flex;
  align-items: center;
  gap: var(--ant-margin-xs);
  cursor: pointer;
`;

const userAvatar = css`
  background-color: var(--ant-color-primary);
  flex-shrink: 0;
`;

const userName = css`
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const contentStyle = css`
  margin: var(--ant-padding);
`;

const contentCard = css`
  min-height: calc(100vh - var(--calculabel-header-h) - calc(var(--ant-padding) * 2));
`;

type Props = {
  readonly children: ReactNode;
  readonly currentUser: UserFragment;
  readonly onLogout: () => void;
  readonly refetchUser: () => Promise<unknown>;
};

export const AppLayout = ({ children, currentUser, onLogout, refetchUser }: Props) => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { modal } = App.useApp();

  const confirmLogout = () =>
    modal.confirm({
      title: t("layout.signOutTitle"),
      content: t("layout.signOutConfirm"),
      okText: t("layout.signOut"),
      okButtonProps: { danger: true, icon: <LogoutOutlined /> },
      cancelButtonProps: { icon: <CloseOutlined /> },
      onOk: onLogout,
    });

  const initials = currentUser.name
    .split(" ")
    .map((part) => part[0] ?? "")
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const sidebarItems: MenuProps["items"] = [
    {
      key: routes.recipes.path,
      icon: <BookOutlined />,
      label: t("layout.recipes"),
      onClick: () => navigate(routes.recipes.to()),
    },
    {
      key: routes.ingredients.path,
      icon: <ExperimentOutlined />,
      label: t("layout.ingredients"),
      onClick: () => navigate(routes.ingredients.to()),
    },
    {
      key: routes.producers.path,
      icon: <ShopOutlined />,
      label: t("layout.producers"),
      onClick: () => navigate(routes.producers.to()),
    },
  ];

  const userMenuItems: MenuProps["items"] = [
    {
      key: routes.myAccount.path,
      icon: <SettingOutlined />,
      label: t("layout.myAccount"),
      onClick: () => navigate(routes.myAccount.to()),
    },
    { type: "divider" },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: t("layout.signOut"),
      onClick: () => {
        confirmLogout();
      },
      danger: true,
    },
  ];

  return (
    <Layout className={root} style={{ "--calculabel-header-h": `${HEADER_HEIGHT}px` } as CSSProperties}>
      <Sider className={siderStyle} collapsed={collapsed} collapsible onCollapse={setCollapsed} theme="light">
        <Flex align="center" className={logoStyle} gap="small" justify="center">
          <LogoSvg className={logoIcon} />
          {!collapsed && (
            <Typography.Text className={logoText} strong>
              Calculabel
            </Typography.Text>
          )}
        </Flex>
        <Flex className={siderInner} vertical>
          <Menu
            className={sidebarMenu}
            items={sidebarItems}
            mode="inline"
            selectedKeys={sidebarItems
              .flatMap((item) => (item != null && typeof item.key === "string" ? [item.key] : []))
              .filter((key) => location.pathname.startsWith(key))}
          />
          <div className={langSwitcherWrap}>
            <LanguageSwitcher collapsed={collapsed} currentUser={currentUser} />
          </div>
        </Flex>
      </Sider>

      <Layout>
        <Header className={headerStyle}>
          <Dropdown destroyOnHidden menu={{ items: userMenuItems }} placement="bottomRight" trigger={["click"]}>
            <Button className={userTrigger} size="large" type="text">
              <Avatar className={userAvatar}>{initials}</Avatar>
              <Typography.Text className={userName}>{currentUser.name}</Typography.Text>
            </Button>
          </Dropdown>
        </Header>

        <FreeAccessBanner role={currentUser.membership?.role ?? TeamRole.Member} />
        <Content className={contentStyle}>
          <Card className={contentCard}>
            <CurrentSessionProvider refetchUser={refetchUser} user={currentUser}>
              <CostDisplayProvider>
                <BreadcrumbTrailProvider>{children}</BreadcrumbTrailProvider>
              </CostDisplayProvider>
            </CurrentSessionProvider>
          </Card>
        </Content>
      </Layout>
    </Layout>
  );
};
